/**
 * Bouncer permission constants for TARS-UI.
 *
 * Single source of truth for all permission keys sent to the Bouncer
 * check-access API. Update key values here when BE finalizes naming.
 */

export const BOUNCER_APP_NAME = "TARS";

export const BOUNCER_PERMISSIONS = {
  // Internal user detection + Invoice Management module access
  ACCOUNTS_PAYABLE_VIEW: "FULFILMENT_ACCOUNTS_PAYABLE_VIEW",
  INVOICE_MANAGEMENT_VIEW: "FULFILMENT_ACCOUNTS_PAYABLE_INVOICE_MANAGEMENT_VIEW",

  // Invoice actions
  INVOICE_EDIT: "FULFILMENT_ACCOUNTS_PAYABLE_INVOICE_EDIT",
  INVOICE_UPLOAD: "FULFILMENT_ACCOUNTS_PAYABLE_INVOICE_UPLOAD",
  INVOICE_DELETE: "FULFILMENT_ACCOUNTS_PAYABLE_INVOICE_DELETE",
  INVOICE_VM_APPROVAL: "FULFILMENT_ACCOUNTS_PAYABLE_INVOICE_VM_APPROVAL",
  INVOICE_L1_APPROVAL: "FULFILMENT_ACCOUNTS_PAYABLE_INVOICE_L1_APPROVAL",
  INVOICE_L2_APPROVAL: "FULFILMENT_ACCOUNTS_PAYABLE_INVOICE_L2_APPROVAL",

  // Credit note approval (maps to L2 for now; swap the value when a dedicated permission is added)
  CREDIT_NOTE_APPROVAL: "FULFILMENT_ACCOUNTS_PAYABLE_INVOICE_L2_APPROVAL",

  // Tab visibility (also used for Invoice Management module gate — OR logic)
  PO_VIEW: "FULFILMENT_ACCOUNTS_PAYABLE_PO_VIEW",
  PAYMENT_VIEW: "FULFILMENT_ACCOUNTS_PAYABLE_PAYMENT_VIEW",

  // Payment actions
  PAYMENT_EDIT: "FULFILMENT_ACCOUNTS_PAYABLE_PAYMENT_EDIT",

  // Vendor wallet — gates the Wallet tab and the header wallet popup (with the
  // vip_wallet flag, see useWalletAccess). Same key the VIP wallet read is
  // authorized against. Must be granted to the relevant roles (incl. vendor
  // roles) in Bouncer before the wallet becomes visible to them.
  //
  // Sourced from window.config.BOUNCER_KEYS.VENDOR_WALLET_VIEW so the slug can be
  // renamed per environment without a code deploy (TUI-G001) — same pattern and
  // same `||` semantics as VENDOR_ONBOARD below. Covered by
  // bouncerPermissions.test.js.
  VENDOR_WALLET_VIEW:
    (typeof window !== "undefined" &&
      window.config?.BOUNCER_KEYS?.VENDOR_WALLET_VIEW) ||
    "FULFILMENT_ACCOUNTS_PAYABLE_VENDOR_WALLET_VIEW",

  // Open advance — records the refund UTR on an expired wallet entry (ENG-27799).
  // Vendor-callable; VIP derives the scope from the caller. Needs granting in
  // Bouncer and a `permissions` entry in each environment's VIP config. Same
  // BOUNCER_KEYS override pattern and `||` semantics as VENDOR_WALLET_VIEW.
  WALLET_REFUND_RECORD:
    (typeof window !== "undefined" &&
      window.config?.BOUNCER_KEYS?.WALLET_REFUND_RECORD) ||
    "FULFILMENT_ACCOUNTS_PAYABLE_WALLET_REFUND_RECORD",

  // Other features
  PAYMENT_UPLOAD: "FULFILMENT_ACCOUNTS_PAYABLE_PAYMENT_UPLOAD",
  SUMMARY_DOWNLOAD: "FULFILMENT_ACCOUNTS_PAYABLE_SUMMARY_DOWNLOAD",

  // Vendor Onboarding — VM invitation screen (module gate + route access).
  // Sourced from window.config.BOUNCER_KEYS.VENDOR_ONBOARD so the Bouncer
  // string can be renamed per environment without a code deploy. Default
  // matches the current provisioned key.
  //
  // Read at module-load time — requires public/config.js to load before the
  // bundle. index.html loads it as a synchronous <script> in <head> ahead of
  // the deferred bundle; keep that ordering or the fallback silently applies.
  //
  // Uses `||` (not `??`) so an empty-string override falls back to the
  // compiled default — the empty-string case is a config bug, not a valid
  // "explicit empty" signal. Covered by bouncerPermissions.test.js.
  VENDOR_ONBOARD:
    (typeof window !== "undefined" &&
      window.config?.BOUNCER_KEYS?.VENDOR_ONBOARD) ||
    "FULFILMENT_ACCOUNTS_PAYABLE_VENDOR_CREATE",

  RFQ_VIEW:
    (typeof window !== "undefined" &&
      window.config?.BOUNCER_KEYS?.RFQ_VIEW) ||
    "FULFILMENT_ACCOUNTS_PAYABLE_RFQ_VIEW",
};

/** Flat array of all permission keys — used when calling the Bouncer API. */
export const ALL_PERMISSION_KEYS = Object.values(BOUNCER_PERMISSIONS);
